import {
  Avatar,
  Box,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';

import { IAttachment } from '../../../interfaces/IAttachment';

import DeleteIcon from '@mui/icons-material/Delete';
import AttachmentIcon from '@mui/icons-material/Attachment';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { sdsGrayscale } from '../../../theme/sds-tokens';
import { InputFile } from './MedicalCertificateForm.styled';
import { BiAddToQueue } from 'react-icons/bi';
import { useMedicalCertificateService } from '../../../hooks/services/useMedicalCertificateService';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';

interface IProps {
  medicalCertificateId: string;
}

const isImage = (filename: string) => /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(filename);

const MedicalCertificateFormAttachments = (props: IProps) => {
  const inputFile = useRef<HTMLInputElement | null>(null);
  const [attachments, setAttachments] = useState<IAttachment[]>([]);
  const [loading, setLoading] = useState(false);

  const medicalCertificateService = useMedicalCertificateService();
  const toastr = useToastrVanila();

  useEffect(() => {
    listAttachments();
  }, [props.medicalCertificateId]);

  const getFilename = (attachment: IAttachment) => {
    const url = attachment.attachment;
    const fileName = url?.split('/')[url.split('/').length - 1];

    return fileName?.substring(33, 999);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    sendFile(e.target.files?.item(0) as File);
  };

  const sendFile = async (fileParam: File | null) => {
    if (fileParam) {
      setLoading(true);
      let file = fileParam;

      const blob = new Blob([fileParam]);

      const tamannhoEmBytes = blob.size;

      if (tamannhoEmBytes > 5242880) {
        setLoading(false);
        toastr.error('O tamanho máximo da imagem é 5MB');
        return;
      }

      if (tamannhoEmBytes >= 1000000 && tamannhoEmBytes <= 5242880 && fileParam.type !== 'application/pdf') {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };

        file = await imageCompression(fileParam, options);
      }

      const data = new FormData();

      data.append('attachment', file, fileParam?.name);
      await medicalCertificateService
        .sendAttachment(`${props.medicalCertificateId}`, data)
        .then(async () => {
          setLoading(false);
          listAttachments();
        })
        .catch(error => {
          setLoading(false);
          toastr.error('Ocorreu um erro ao enviar a imagem. Tente novamente mais tarde.');
        });
    }
  };

  const listAttachments = async () => {
    medicalCertificateService
      .listAttachmentsByMedicalCertificateId(props.medicalCertificateId)
      .then(result => {
        setAttachments(result);
      })
      .catch(() => {
        toastr.error('Ocorreu um erro ao listar os anexos. Tente novamente mais tarde.');
      });
  };

  const removeAttachment = async (id: string): Promise<void> => {
    setLoading(true);
    medicalCertificateService
      .removeAttachment(id)
      .then(async () => {
        setLoading(false);
        await listAttachments();
      })
      .catch(() => {
        setLoading(false);
        toastr.error('Ocorreu um erro ao carregar a lista de doenças.');
      });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        mt: 1,
        bgcolor: theme => (theme.palette.mode === 'dark' ? theme.palette.background.paper : sdsGrayscale[10]),
      }}
    >
      <InputFile type="file" ref={inputFile} onChange={onFileChange} accept="application/pdf,image/*" />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachmentIcon color="info" />
          <Typography variant="h2" component="h2" fontWeight={600}>
            Anexos
          </Typography>
        </Box>
        <Tooltip title="Adicionar Anexos" placement="left">
          <IconButton onClick={() => inputFile.current?.click()}>
            <BiAddToQueue/>
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ height: 4, mt: 1 }}>{loading ? <LinearProgress /> : <></>}</Box>
      <Divider />
      <List dense={true} sx={{ mt: 1 }}>
        {attachments.map(attachment => {
          const filename = getFilename(attachment) ?? '';

          return (
            <ListItem
              key={attachment.id}
              secondaryAction={
                <Tooltip title="Excluir anexo" placement="left">
                  <IconButton edge="end" aria-label="delete" onClick={() => removeAttachment(attachment.id)}>
                    <DeleteIcon color='error'/>
                  </IconButton>
                </Tooltip>
              }
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                pr: 7,
              }}
            >
              <ListItemAvatar>
                <Avatar
                  variant="rounded"
                  sx={{ width: 40, height: 40, bgcolor: 'action.hover', color: 'info.main' }}
                >
                  {isImage(filename) ? (
                    <img
                      alt={filename}
                      style={{ height: '100%', width: '100%', objectFit: 'cover' }}
                      src={attachment.attachment}
                    />
                  ) : (
                    <InsertDriveFileOutlinedIcon fontSize="small" />
                  )}
                </Avatar>
              </ListItemAvatar>
              <ListItemText>
                <Link
                  href={attachment.attachment}
                  target="_blank"
                  rel="noopener"
                  underline="hover"
                  sx={{ display: 'block', fontWeight: 600, wordBreak: 'break-all' }}
                >
                  {filename}
                </Link>
              </ListItemText>
            </ListItem>
          );
        })}

        {attachments.length === 0 && (
          <Typography variant="helper" sx={{ display: 'block', color: 'text.secondary', py: 1 }}>
            Nenhum anexo enviado.
          </Typography>
        )}
      </List>
    </Paper>
  );
};

export default MedicalCertificateFormAttachments;
